import React from "react";
import { withRouter } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import api from "../../api/api";
import Header from "../../components/Header";
import withRecoil from "../../withRecoil";
import { accessTokenState } from "../../atoms";
import { Modal, Spin } from 'antd';

class VenmoPayment extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            amount: "10.00",
            feeSchedule: null,
            fee: 0,
            total: 0,
            errorMessage: "",
            orderId: null,
            isLoading: true
        };
    }

    componentDidMount() {
        this.fetchFeeSchedule();
    }

    fetchFeeSchedule = async () => {
        try {
            if (!this.props.recoilStates.accessTokenState.state) {
                throw new Error("Access token is missing");
            }

            const amount = this.state.amount || "10.00";
            const accessToken = this.props.recoilStates.accessTokenState.state;
            const url = `${api.main}/get_fee_schedule?amount=${encodeURIComponent(amount)}&access_token=${encodeURIComponent(accessToken)}`;

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Accept": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error(`Unexpected content type: ${contentType}`);
            }

            const data = await response.json();

            if (data.status === 105 && data.data && data.data.length > 0) {
                const paypalFeeSchedules = data.data.filter(schedule => schedule.source === "PayPal");

                if (paypalFeeSchedules.length > 0) {
                    this.setState({
                        feeSchedule: paypalFeeSchedules
                    }, () => {
                        this.calculatePaypalFee();
                    });
                } else {
                    console.error("No PayPal fee schedules found.");
                    this.setState({ feeSchedule: [] });
                }
            } else {
                this.setState({ feeSchedule: [] });
            }

        } catch (error) {
            console.error('Failed to fetch fee schedule:', error);
            this.setState({ isLoading: false }, () => {
                this.calculatePaypalFee();
            });
        }
    };

    handleAmountChange = (event) => {
        const newAmount = event.target.value;

        if (parseFloat(newAmount) < 10) {
            this.setState({
                amount: newAmount,
                errorMessage: "Please input an amount of $10 or greater."
            });
        } else {
            this.setState({
                amount: newAmount,
                errorMessage: "",
                isLoading: true
            }, () => {
                this.fetchFeeSchedule();
            });
        }
    };

    createOrder = async () => {
        try {
            // console.log("First log in create order");
            if (parseFloat(this.state.amount) < 10) {
                alert("Please enter an amount of $10 or greater.");
                return null;
            }

            if (!this.props.recoilStates.accessTokenState.state) {
                throw new Error("Access token is missing");
            }

            // console.log("What is being sent by createOrder, amount, fee and total: ", this.state.amount, this.state.fee, this.state.total);
            // console.log("Total type is: ", typeof this.state.total);

            const amount = (parseFloat(this.state.amount))
            const fee = (parseFloat(this.state.fee))
            const total = (parseFloat(this.state.total))

            const formattedAmount = amount.toFixed(2);
            const formattedFee = fee.toFixed(2);
            const formattedTotal = total.toFixed(2);

            // Send the total amount (amount + fee)
            const body = {
                amount: formattedAmount,
                fee: formattedFee,
                total: formattedTotal,
                access_token: this.props.recoilStates.accessTokenState.state
            };

            // console.log("After: ", body);

            const response = await fetch(`${api.extra}/api/paypal/create_order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(body),
            });

            const data = await response.json();
            // console.log("Data from create order: ", data);

            if (!response.ok) {
                throw new Error(data.error || `HTTP error! Status: ${response.status}`);
            }

            if (!data.orderId) {
                throw new Error("Order ID missing from response");
            }

            await this.setState({
                orderId: data.orderId,
            })

            return data.orderId;
        } catch (error) {
            alert("Failed to create order. Please try again.");
            return null;
        }
    };

    captureOrder = async () => {
        try {
            const accessToken = this.props.recoilStates.accessTokenState.state;

            if (!accessToken) {
                throw new Error("Access token is missing");
            }

            const response = await fetch(`${api.extra}/api/paypal/capture_order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ orderId: this.state.orderId, access_token: accessToken }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || `HTTP error! Status: ${response.status}`);
            }

            if (this.props.onSuccess) this.props.onSuccess(data);

            if (this.props.history) {
                Modal.success({
                    title: "Success",
                    content: "Your transaction has been submitted for processing. Please allow up to 3 minutes for your balance to be updated. You will receive an sms notification once it is complete.",
                    onOk: () => {
                        this.props.history.push({
                            pathname: "/dashboard"
                        })
                    }
                });
            }
        } catch (error) {
            console.error("Error capturing order", error);
            if (this.props.onError) this.props.onError(error);
        }
    };

    calculatePaypalFee = async () => {
        const amount = parseFloat(this.state.amount);

        if (isNaN(amount) || amount < 10) {
            this.setState({ fee: 0, total: 0, isLoading: false });
            return;
        }

        if (!this.state.feeSchedule || !Array.isArray(this.state.feeSchedule)) {
            console.error("Fee schedule is invalid");
            return;
        }

        const applicableFee = this.state.feeSchedule.find(schedule => {
            const start = parseFloat(schedule.amount_start);
            const end = schedule.amount_end !== null ? parseFloat(schedule.amount_end) : Infinity;

            return amount >= start && amount <= end;
        });

        if (applicableFee) {
            const fee = applicableFee.flat_rate + (amount * applicableFee.percentage / 100);
            const total = amount + fee;

            await this.setState({
                fee,
                total,
                isLoading: false
            });
        }
    }

    render() {
        return (
            <PayPalScriptProvider options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID }}>
            <Header />
                <div className="spare-dvs" style={{minHeight: "calc(100vh - 80px)"}}>
                <div style={{ maxWidth: '600px', margin: '0 auto', padding: '10px' }}>
                    
                    {/* Back Button + Centered Header */}
                    <div style={{ position: 'relative', marginBottom: '20px', textAlign: 'center', width: '100%' }}>
                    <button
                        onClick={() => this.props.history.push('/dashboard')}
                        style={{
                        position: 'absolute',
                        left: '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer'
                        }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 19l-7-7 7-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <line x1="8" y1="12" x2="20" y2="12" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <h2 style={{ margin: '0' }}>Add to Balance</h2>
                    </div>

                    {/* Amount Input */}
                    <div style={{ marginBottom: "15px" }}>
                    <label style={{ fontSize: "18px" }}>
                        Amount: $
                        <input
                        type="number"
                        value={this.state.amount}
                        onChange={this.handleAmountChange}
                        step="0.01"
                        min="10.00"
                        style={{
                            marginLeft: "10px",
                            padding: "8px",
                            fontSize: "16px",
                            borderRadius: "5px",
                            border: "1px solid #ccc",
                            width: "120px",
                            textAlign: "center",
                        }}
                        />
                    </label>
                    {this.state.errorMessage && (
                        <div style={{ color: "red", fontSize: "14px", marginTop: "5px" }}>
                        {this.state.errorMessage}
                        </div>
                    )}
                    <p style={{ color: "orange" }}>
                        Transaction fee: ${this.state.fee.toFixed(2)}
                    </p>
                    <p style={{ color: "black" }}>
                        Total: ${this.state.total.toFixed(2)}
                    </p>
                    </div>

                    {/* PayPal Button */}
                    <div style={{ width: "300px", margin: "0 auto" }}>
                    {this.state.isLoading ? (
                        <div style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px"
                        }}>
                        <Spin size="large" tip="Calculating fees..." />
                        </div>
                    ) : (
                        <PayPalButtons
                        style={{ layout: "vertical" }}
                        createOrder={this.createOrder}
                        onApprove={this.captureOrder}
                        onError={() => alert("Payment failed. Please try again.")}
                        />
                    )}
                    </div>
                </div>
                </div>
            </PayPalScriptProvider>
        );
    }
}

export default withRecoil(withRouter(VenmoPayment), [accessTokenState]);
